import { getCtx } from './context.js';

export interface MusicPlayer {
  start(): Promise<void>;
  toggle(enabled: boolean): void;
}

export function createMusicPlayer(
  tracks: string[],
  isMusicEnabled: () => boolean,
  volume: number,
): MusicPlayer {
  let musicAudio: HTMLAudioElement | null = null;
  let musicGain: GainNode | null = null;
  let musicSource: MediaElementAudioSourceNode | null = null;
  let currentTrackIndex = -1;
  let initialized = false;
  let fadeOutTimer: ReturnType<typeof setTimeout> | null = null;

  function clearMediaSession(): void {
    if (!navigator.mediaSession) return;
    navigator.mediaSession.metadata = null;
    const actions: MediaSessionAction[] = [
      'play', 'pause', 'seekbackward', 'seekforward',
      'previoustrack', 'nexttrack', 'stop',
    ];
    for (const action of actions) {
      try { navigator.mediaSession.setActionHandler(action, null); } catch { /* unsupported */ }
    }
  }

  function init(): { audio: HTMLAudioElement; gain: GainNode } {
    if (!musicAudio) {
      const c = getCtx();
      musicAudio = new Audio();
      musicAudio.loop = true;
      musicAudio.crossOrigin = 'anonymous';
      musicSource = c.createMediaElementSource(musicAudio);
      musicGain = c.createGain();
      musicSource.connect(musicGain);
      musicGain.connect(c.destination);
      musicGain.gain.value = 0;

      // Prevent iOS from showing the media widget
      clearMediaSession();
      musicAudio.addEventListener('play', clearMediaSession);

      // Auto-pause on visibility change
      document.addEventListener('visibilitychange', () => {
        if (!initialized || !musicAudio || !musicGain) return;
        if (!isMusicEnabled()) return;

        const c2 = getCtx();
        if (document.hidden) {
          musicGain.gain.setValueAtTime(musicGain.gain.value, c2.currentTime);
          musicGain.gain.linearRampToValueAtTime(0, c2.currentTime + 0.3);
          if (fadeOutTimer) clearTimeout(fadeOutTimer);
          fadeOutTimer = setTimeout(() => {
            musicAudio?.pause();
            fadeOutTimer = null;
          }, 400);
        } else {
          if (fadeOutTimer) { clearTimeout(fadeOutTimer); fadeOutTimer = null; }
          musicAudio.play().then(() => {
            musicGain!.gain.setValueAtTime(0, c2.currentTime);
            musicGain!.gain.linearRampToValueAtTime(volume, c2.currentTime + 0.5);
          }).catch(() => {});
        }
      });
    }
    return { audio: musicAudio, gain: musicGain! };
  }

  return {
    async start(): Promise<void> {
      if (initialized) return;
      if (!isMusicEnabled()) return;

      const { audio, gain } = init();
      const c = getCtx();

      currentTrackIndex = Math.floor(Math.random() * tracks.length);
      audio.src = tracks[currentTrackIndex];
      await audio.play();
      initialized = true;
      gain.gain.setValueAtTime(0, c.currentTime);
      gain.gain.linearRampToValueAtTime(volume, c.currentTime + 2);

      audio.onended = () => {
        currentTrackIndex = (currentTrackIndex + 1) % tracks.length;
        audio.src = tracks[currentTrackIndex];
        audio.play().catch(() => {});
      };
    },

    toggle(enabled: boolean): void {
      const { audio, gain } = init();
      const c = getCtx();

      if (fadeOutTimer) { clearTimeout(fadeOutTimer); fadeOutTimer = null; }

      if (enabled) {
        if (!initialized || audio.paused) {
          if (currentTrackIndex < 0) {
            currentTrackIndex = Math.floor(Math.random() * tracks.length);
            audio.src = tracks[currentTrackIndex];
          }
          audio.play().then(() => {
            initialized = true;
            gain.gain.setValueAtTime(0, c.currentTime);
            gain.gain.linearRampToValueAtTime(volume, c.currentTime + 1);
          }).catch(() => {});
        } else {
          gain.gain.setValueAtTime(gain.gain.value, c.currentTime);
          gain.gain.linearRampToValueAtTime(volume, c.currentTime + 0.5);
        }
      } else {
        gain.gain.setValueAtTime(gain.gain.value, c.currentTime);
        gain.gain.linearRampToValueAtTime(0, c.currentTime + 0.5);
        fadeOutTimer = setTimeout(() => {
          audio.pause();
          fadeOutTimer = null;
        }, 600);
      }
    },
  };
}
